import { useState } from 'react';
import { Trans, useLingui } from '@lingui/react/macro';

import ClientForm, {
  type ClientState,
  getClientError,
  useClientState,
} from '@/components/ClientForm';
import ContactDetails from '@/components/ContactDetails';
import { useNavigate } from '@/components/Link/Link';
import { UpdateStore } from '@/components/Store/UpdateStore';
import Box from '@/design_system/Box';
import Button from '@/design_system/Button';
import Message from '@/design_system/Message';
import Stack from '@/design_system/Stack';
import Title from '@/design_system/Typography/Title';
import { useBrandRequestContext } from '@/layouts/Brand/BrandRequestContext';
import { useSendClientRequest, useUpdateRequestClient } from '@/models/request';
import { Store, useStore, useUpdateExternalStore } from '@/models/store';
import CartContent from '@/routes/Brand/Requests/New/components/CartContent';
import { useClientToken, useCurrentOrganization, useStoreToken } from '@/services/auth';
import { createBEMClasses } from '@/utils/classname';
import { REQUEST_IN_PROGRESS_LOCAL_STORAGE_KEY } from '@/utils/requestInProgress';

import './Checkout.css';

const { block, element } = createBEMClasses('client-new-request-checkout');

const Checkout = ({ onEditCart }: { onEditCart: () => void }) => {
  const { t } = useLingui();
  const navigate = useNavigate();
  const [organization] = useCurrentOrganization();
  const clientToken = useClientToken();
  const storeToken = useStoreToken();
  const { request, view } = useBrandRequestContext();
  const { data: store } = useStore(request.storeId ?? undefined);
  const isExternalStoreRequest = !clientToken;

  const { mutateAsync: updateRequestClient } = useUpdateRequestClient(request.id);
  const clientState = useClientState();

  const [showErrors, setShowErrors] = useState(false);
  const [apiError, setApiError] = useState('');

  const { mutateAsync: sendClientRequest, isPending: isSendClientRequestPending } =
    useSendClientRequest();

  const handleSendRequest = async () => {
    setShowErrors(true);
    setApiError('');

    try {
      if (request.client) {
        if (getClientError({ client: clientState, view: view.client }).hasError) {
          return;
        }

        await updateRequestClient({
          client: {
            name: clientState.name,
            title: clientState.title,
            firstName: clientState.firstName,
            lastName: clientState.lastName,
            email: clientState.email,
            address: clientState.address ?? undefined,
            billingAddress: clientState.billingAddress ?? undefined,
            phone: clientState.phone,
            deliveryOption: isExternalStoreRequest ? clientState.deliveryOption : undefined,
            sendcloudServicePointId: isExternalStoreRequest
              ? clientState.sendcloudServicePointId
              : undefined,
            locale: isExternalStoreRequest ? clientState.locale : undefined,
          },
        });
      }

      await sendClientRequest({
        id: request.id,
      });
    } catch (err: any) {
      console.error(err);

      if ((err.message as string).includes('The selected country is not supported')) {
        setApiError(
          t({
            id: 'client.new.checkout.send.country-error',
            message: 'The selected country is not supported',
          })
        );
      } else {
        setApiError(
          (err.message as string) ?? t({ id: '_general.error.unknown', message: 'Unknown error' })
        );
      }

      return;
    }

    const tokenQueryParam = clientToken ? `clientToken=${clientToken}` : `storeToken=${storeToken}`;

    window.localStorage.removeItem(REQUEST_IN_PROGRESS_LOCAL_STORAGE_KEY);

    navigate(`/brand/${organization?.slug}/requests/${request.id}?${tokenQueryParam}`, {
      replace: true, // Replace the current entry (`/new?requestId=123`) to make sure the user can't go back to the checkout page
    });
  };

  return (
    <div className={block()}>
      <main>
        <div className={element('content')}>
          <div className={element('cart')}>
            <CartContent onEditCart={onEditCart} recap />
          </div>
          <Stack gap="1.5rem" className={element('client-form')}>
            {request.client ? (
              <ClientCheckoutForm
                clientState={clientState}
                showErrors={showErrors}
                isExternalStoreRequest={isExternalStoreRequest}
              />
            ) : store ? (
              <StoreOnlyCheckoutForm store={store} />
            ) : null}
          </Stack>
        </div>
      </main>
      <footer>
        {!!apiError && (
          <Message type="error" style={{ marginBottom: '0.5rem' }}>
            {apiError}
          </Message>
        )}
        <Stack row>
          <Button
            size="large"
            variant="brand"
            style={{ flex: 1 }}
            onPress={handleSendRequest}
            isLoading={isSendClientRequestPending}
          >
            <Trans id="client.new.checkout.send">Send request</Trans>
          </Button>
        </Stack>
      </footer>
    </div>
  );
};

const ClientCheckoutForm = ({
  clientState,
  showErrors,
  isExternalStoreRequest,
}: {
  clientState: ClientState;
  showErrors: boolean;
  isExternalStoreRequest: boolean;
}) => {
  return (
    <>
      <Title size="300" as="h1">
        {isExternalStoreRequest ? (
          <Trans id="client.new.checkout.external-store.title">Client information</Trans>
        ) : (
          <Trans id="client.new.checkout.client.title">Please fill your information</Trans>
        )}
      </Title>

      <ClientForm
        state={clientState}
        hideVip
        hideLanguage={!isExternalStoreRequest}
        hideDeliveryOption={!isExternalStoreRequest}
        variant="brand"
        size="large"
        showErrors={showErrors}
      />
    </>
  );
};

const StoreOnlyCheckoutForm = ({ store }: { store: Store }) => {
  const { mutateAsync: updateExternalStore, isPending: isUpdateExternalStorePending } =
    useUpdateExternalStore();

  return (
    <>
      <Stack row justifyContent="space-between" alignItems="center">
        <Title size="300" as="h1">
          <Trans id="client.new.checkout.external-store.store-only.title">Store information</Trans>
        </Title>
        <UpdateStore
          store={store}
          isPending={isUpdateExternalStorePending}
          updateStore={async (store) => {
            await updateExternalStore(store);
          }}
          variant="brand"
          size="large"
        />
      </Stack>

      <Box gap="0.75rem" padding="12px 16px" style={{ flex: 'unset' }}>
        <ContactDetails
          type="store"
          name={store.name}
          email={store.address?.contactEmail}
          address={store.address}
          phone={store.contactInfo?.phone}
          openingHours={store.storeConfig?.openingHours}
        />
      </Box>
    </>
  );
};

export default Checkout;
